import React, { useState, useEffect, useCallback } from 'react';
import {
    View, Text, TouchableOpacity, StyleSheet, Image, ScrollView,
} from 'react-native';
import { IActivity, StoryReaderActivityConfig, StoryPage, KeyStage } from '@eduwrite/shared';
import { useStageTokens } from '../hooks/useStageTokens';
import { useAudioPlayback } from '../hooks/useAudioPlayback';
import { AudioPromptButton } from './AudioPromptButton';
import { FriendlyButton } from './FriendlyButton';

interface StoryReaderActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/**
 * StoryReaderActivity — linear page-by-page illustrated story reader.
 *
 * Renders ONE page at a time (page-deck, not ScrollView of all pages).
 * Large Next/Prev controls (56px min, 72px for Reception per rules/09).
 * Auto-plays narration on page enter if narrationAudioUrl is set.
 * Completion = all pages visited → calls onComplete.
 *
 * Must live in src/components/ (NOT src/components/activities/ — the stale
 * directory deleted by WP-053).
 */
export const StoryReaderActivity: React.FC<StoryReaderActivityProps> = ({
    activity,
    onComplete,
    keyStage,
}) => {
    const config = (activity.config || {}) as StoryReaderActivityConfig;
    const pages: StoryPage[] = Array.isArray(config.pages) ? config.pages : [];
    const tokens = useStageTokens(keyStage);

    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [visitedPageIds, setVisitedPageIds] = useState<Set<string>>(new Set());

    const currentPage = pages[currentPageIndex];
    const isLastPage = currentPageIndex === pages.length - 1;
    const isFirstPage = currentPageIndex === 0;

    // Audio playback for narration
    const narrationUrl = currentPage?.narrationAudioUrl || '';
    const audio = useAudioPlayback(narrationUrl);

    // Track visited pages
    useEffect(() => {
        if (currentPage) {
            setVisitedPageIds(prev => {
                if (prev.has(currentPage.id)) return prev;
                const next = new Set(prev);
                next.add(currentPage.id);
                return next;
            });
        }
    }, [currentPage]);

    // Auto-play narration on page enter (EYFS auto-plays, others tap to play)
    useEffect(() => {
        if (narrationUrl && tokens.autoPlayAudio) {
            audio.play();
        }
    }, [currentPageIndex, narrationUrl]);

    const handleNext = useCallback(() => {
        if (currentPageIndex < pages.length - 1) {
            setCurrentPageIndex(currentPageIndex + 1);
        }
    }, [currentPageIndex, pages.length]);

    const handlePrev = useCallback(() => {
        if (currentPageIndex > 0) {
            setCurrentPageIndex(currentPageIndex - 1);
        }
    }, [currentPageIndex]);

    const handleFinish = useCallback(() => {
        // Ensure all pages are marked as visited
        const allVisited = new Set(visitedPageIds);
        pages.forEach(p => allVisited.add(p.id));
        onComplete();
    }, [onComplete, pages, visitedPageIds]);

    // Graceful audio error handling — kind visible message, never silent
    const audioErrorMessage = audio.error
        ? 'Let\'s try again! The audio didn\'t load.'
        : null;

    // Age-adaptive text sizing: 28px for EYFS, 20px for KS2, 24px for KS1
    const storyFontSize = tokens.isEYFS ? 28 : tokens.isKS2 ? 20 : 24;

    if (!currentPage) {
        return (
            <View style={styles.errorContainer} accessibilityRole="alert" accessibilityLabel="Story has no pages">
                <Text style={styles.errorText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    This story has no pages yet.
                </Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {/* Story title */}
            {config.title && (
                <View style={styles.titleContainer}>
                    <Text style={[styles.title, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {config.title}
                    </Text>
                    {config.author && (
                        <Text style={styles.author} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            by {config.author}
                        </Text>
                    )}
                </View>
            )}

            {/* Page progress indicator */}
            <View style={styles.progressContainer}>
                <Text style={styles.progressText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    Page {currentPageIndex + 1} of {pages.length}
                </Text>
            </View>

            {/* Page content — single page at a time (page-deck rendering) */}
            <ScrollView
                style={styles.pageContainer}
                contentContainerStyle={styles.pageContent}
                showsVerticalScrollIndicator={false}
            >
                {/* Optional page image */}
                {currentPage.imageUrl ? (
                    <Image
                        testID={`story-image-${currentPage.id}`}
                        source={{ uri: currentPage.imageUrl }}
                        style={[styles.pageImage, { height: tokens.imageHeight }]}
                        resizeMode="contain"
                        accessibilityLabel={`Illustration for page ${currentPageIndex + 1}`}
                    />
                ) : null}

                {/* Page text */}
                <Text
                    testID={`story-text-${currentPage.id}`}
                    style={[styles.pageText, { fontSize: storyFontSize, lineHeight: storyFontSize * 1.4 }]}
                    allowFontScaling={true}
                    maxFontSizeMultiplier={1.5}
                >
                    {currentPage.text}
                </Text>

                {/* Vocabulary chips (KS2 word bank) */}
                {currentPage.vocabulary && currentPage.vocabulary.length > 0 && (
                    <View style={styles.vocabContainer}>
                        {currentPage.vocabulary.map((word, idx) => (
                            <View key={idx} style={styles.vocabChip}>
                                <Text style={styles.vocabText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                    {word}
                                </Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* Narration audio controls */}
                {narrationUrl && (
                    <View style={styles.audioContainer}>
                        <AudioPromptButton
                            text={`Listen to page ${currentPageIndex + 1}`}
                            autoPlay={false}
                        />
                        {audioErrorMessage && (
                            <Text
                                testID={`story-audio-error-${currentPage.id}`}
                                style={styles.audioErrorText}
                                allowFontScaling={true}
                                maxFontSizeMultiplier={1.5}
                            >
                                {audioErrorMessage}
                            </Text>
                        )}
                    </View>
                )}
            </ScrollView>

            {/* Navigation controls */}
            <View style={styles.navContainer}>
                {/* Prev button — hidden on first page */}
                {!isFirstPage ? (
                    <TouchableOpacity
                        testID="story-prev-btn"
                        onPress={handlePrev}
                        style={[
                            styles.navButton,
                            styles.prevButton,
                            { minHeight: tokens.touchTargetMin, minWidth: tokens.touchTargetMin },
                        ]}
                        activeOpacity={0.75}
                        accessibilityLabel="Previous page"
                        accessibilityRole="button"
                    >
                        <Text style={styles.navButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            ← Back
                        </Text>
                    </TouchableOpacity>
                ) : (
                    <View style={styles.navButtonPlaceholder} />
                )}

                {/* Next or Finish button */}
                {isLastPage ? (
                    <FriendlyButton
                        variant="primary"
                        size="large"
                        onPress={handleFinish}
                        testID="story-finish-btn"
                        accessibilityLabel="Finish story"
                    >
                        FINISH ✓
                    </FriendlyButton>
                ) : (
                    <TouchableOpacity
                        testID="story-next-btn"
                        onPress={handleNext}
                        style={[
                            styles.navButton,
                            styles.nextButton,
                            { minHeight: tokens.touchTargetMin, minWidth: tokens.touchTargetMin },
                        ]}
                        activeOpacity={0.75}
                        accessibilityLabel="Next page"
                        accessibilityRole="button"
                    >
                        <Text style={styles.navButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            Next →
                        </Text>
                    </TouchableOpacity>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    titleContainer: {
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 8,
        alignItems: 'center',
    },
    title: {
        fontWeight: '900',
        color: '#1E293B',
        textAlign: 'center',
    },
    author: {
        fontSize: 14,
        color: '#64748B',
        marginTop: 4,
        fontStyle: 'italic',
    },
    progressContainer: {
        alignItems: 'center',
        paddingVertical: 8,
    },
    progressText: {
        fontSize: 14,
        color: '#64748B',
        fontWeight: '600',
    },
    pageContainer: {
        flex: 1,
    },
    pageContent: {
        paddingHorizontal: 24,
        paddingVertical: 16,
        flexGrow: 1,
    },
    pageImage: {
        width: '100%',
        borderRadius: 16,
        marginBottom: 16,
    },
    pageText: {
        color: '#1E293B',
        fontWeight: '600',
        textAlign: 'center',
        marginBottom: 12,
    },
    vocabContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 8,
        marginTop: 12,
    },
    vocabChip: {
        backgroundColor: '#F0FDF4',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: '#86EFAC',
    },
    vocabText: {
        fontSize: 14,
        color: '#15803D',
        fontWeight: '700',
    },
    audioContainer: {
        alignItems: 'center',
        marginTop: 12,
    },
    audioErrorText: {
        fontSize: 14,
        color: '#FAAD14',
        fontWeight: '600',
        marginTop: 8,
        textAlign: 'center',
    },
    navContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderTopWidth: 1.5,
        borderTopColor: '#BFDBFE',
    },
    navButton: {
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    prevButton: {
        backgroundColor: '#FFFFFF',
        borderWidth: 2,
        borderColor: '#CBD5E1',
    },
    nextButton: {
        backgroundColor: '#1890FF',
    },
    navButtonText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    navButtonPlaceholder: {
        minWidth: 56,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#FEF2F2',
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#EF4444',
        margin: 16,
    },
    errorText: {
        fontSize: 18,
        color: '#DC2626',
        fontWeight: 'bold',
    },
});
